import { memo } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { IconFolderPlusOutline18 as FolderPlus } from "nucleo-ui-outline-18"
import { useForm } from "react-hook-form"
import { z } from "zod"

import type { HelpCenterArticle, HelpCenterGroup } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import {
	Button,
	IconButton,
	Input,
	Popover,
	useBreakpoints,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { RoleCheck } from "@/components/shared/role-check"
import { SidebarAddButton } from "@/components/shared/sidebar-add-button"
import { analytics } from "@/lib/analytics"
import { generateId } from "@/lib/id"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { getActivePortalInstanceId } from "@/stores/active-portal-instance-store"
import { useDocNavStore } from "@/stores/docs/docs-nav-store"
import { useDocsTabs } from "./use-docs-tabs"

const addGroupSchema = z.object({
	groupName: z.string().trim().min(1, "Group name is required"),
})

type AddGroupFormData = z.infer<typeof addGroupSchema>

export const AddGroupButton = memo(
	({
		groups,
		variant = "row",
	}: {
		groups: readonly (HelpCenterGroup & {
			helpCenterArticles: readonly HelpCenterArticle[]
		})[]
		/** `row` is the full-width sidebar row; `icon` is a plus for a section header. */
		variant?: "row" | "icon"
	}) => {
		const navigate = useNavigate()
		const {
			isAddGroupOpen: isOpen,
			setIsAddGroupOpen: setIsOpen,
			openGroup,
		} = useDocNavStore()
		const zero = useZero()
		const workspace = useZeroWorkspace()
		const { activeTabId } = useDocsTabs()
		const { isMobile } = useBreakpoints()
		const form = useForm<AddGroupFormData>({
			resolver: zodResolver(addGroupSchema),
			defaultValues: {
				groupName: "",
			},
		})

		const onSubmit = form.handleSubmit(async (data) => {
			if (!workspace) {
				return
			}
			const id = generateId()

			const activePortalInstanceId = getActivePortalInstanceId()

			await mutateOk(
				zero.mutate(
					mutators.helpCenterGroups.create({
						id,
						order: groups.length,
						name: data.groupName,
						portalInstanceId: activePortalInstanceId,
						tabId: activeTabId,
					}),
				),
			)

			const articleId = generateId()
			await mutateOk(
				zero.mutate(
					mutators.helpCenterArticles.create({
						id: articleId,
						title: "",
						helpCenterGroupId: id,
						parentArticleId: null,
						order: 0,
						archived: false,
						content: {},
						date: null,
						icon: "Page2",
						imageBlurhash: null,
						imageUrl: null,
						published: false,
						showOnHomePage: false,
						summary: "",
						urlName: "",
						portalInstanceId: activePortalInstanceId,
					}),
				),
			)

			analytics.track("add-helpcenter-group", {
				groupName: data.groupName,
				popover: true,
			})
			openGroup(id)
			form.reset()
			setIsOpen(false)

			void navigate({ to: "/edit-docs/$id", params: { id: articleId } })
		})

		const onCancel = () => {
			form.reset()
			setIsOpen(false)
		}

		return (
			<RoleCheck not={["VIEWER"]}>
				<Popover
					className="w-[237px] p-2"
					triggerTooltip={variant === "icon" ? "New group" : undefined}
					open={isOpen}
					onOpenChange={(state) =>
						state === false ? onCancel() : setIsOpen(true)
					}
					content={
						<form onSubmit={onSubmit} className="space-y-2">
							<Input
								{...form.register("groupName")}
								type="text"
								placeholder="Group name"
								className="w-full text-sm text-primary outline-none"
							/>
							{form.formState.errors.groupName && (
								<p className="text-xs text-red-500">
									{form.formState.errors.groupName.message}
								</p>
							)}
							<Button
								type="submit"
								variant="ghost"
								className="w-full rounded-md border p-2"
							>
								Add group
							</Button>
						</form>
					}
				>
					{variant === "icon" ? (
						<IconButton
							Icon={FolderPlus}
							variant="ghost"
							size="sm"
							aria-label="New group"
							className={cn(
								"h-5 w-5 flex-none text-tertiary hover:text-primary",
								// Stays put while its popover is open, otherwise the trigger
								// disappears from under the pointer.
								isOpen || isMobile
									? "opacity-100"
									: "opacity-0 group-hover/section:opacity-100",
							)}
						/>
					) : (
						<SidebarAddButton
							label="New group"
							visible={isMobile || isOpen}
							hoverGroup="sidebar"
						/>
					)}
				</Popover>
			</RoleCheck>
		)
	},
)
